import { useEffect, useState } from 'react';
import axios from 'axios';
import NewCommonSelect from '../../../component/common/selectBox/NewCommonSelect';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';

import '../../../css/modal.css';

/**
 * 전송기준 예외관리 등록/수정 레이어
 * @isModalOpen { modalState, smode('I'|'U'), row } 그리드에서 선택한 행 정보
 * @modalClose 레이어 닫기
 * @setReSearch 저장/삭제 후 목록 재조회
 */
const BdExceptTrans_layer = ({ isModalOpen, modalClose, setReSearch }) => {

    const smode = isModalOpen.smode;
    const row = isModalOpen.row;
    const isUpdate = smode === 'U';

    const showNotiModal = useModalStore(state => state.showNotiModal);
    const confirm = useConfirm();
    const { primaryBackColor } = useDynamicStyle();

    const initForm = {
        searchGrdc: '',
        searchLoca: '',
        searchEmog: '',
        emmgFtdt: '',
        emmgFttm: '00',
        emmgTodt: '',
        emmgTotm: '23',
        emmgFidt: '',
        emmgTidt: '',
        emogAll: 'N',
        emmgDtcd: '1',
        emmgMemo: '',
        emmgFtdtOld: '',
        emmgTodtOld: '',
    };

    const [form, setForm] = useState(initForm);

    useEffect(() => {
        if (!isModalOpen.modalState) return;

        if (isUpdate && row) {
            setForm({
                searchGrdc: row.EMOGGRDC || '',
                searchLoca: row.EMOGLOCA || '',
                searchEmog: row.EMMGEMCD || '',
                emmgFtdt: row.EMMGFTDT || '',
                emmgFttm: row.EMMGFTTM || '00',
                emmgTodt: row.EMMGTODT || '',
                emmgTotm: row.EMMGTOTM || '23',
                emmgFidt: row.EMMGFIDT || '',
                emmgTidt: row.EMMGTIDT || '',
                emogAll: row.EMMGTOAY === 'Y' ? 'Y' : 'N',
                emmgDtcd: row.EMMGDTCD || '1',
                emmgMemo: row.EMMGMEMO || '',
                emmgFtdtOld: row.EMMGFTDT || '',
                emmgTodtOld: row.EMMGTODT || '',
            });
        } else {
            setForm(initForm);
        }
    }, [isModalOpen.modalState]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
        }));
    };

    // 00시 ~ 23시
    const timeOptions = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

    const todayStr = () => {
        const d = new Date();
        return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    };

    // 화면 검증 (서버 검증과 동일 규칙)
    const validate = () => {
        const ftdt = form.emmgFtdt.replace(/-/g, '');
        const todt = form.emmgTodt.replace(/-/g, '');
        const fidt = form.emmgFidt.replace(/-/g, '');
        const tidt = form.emmgTidt.replace(/-/g, '');

        if (form.emogAll !== 'Y' && !form.searchEmog) return '기관을 선택해주세요.';
        if (!ftdt || !todt) return '전송마감예외기간을 입력해주세요.';
        if (!fidt || !tidt) return '전송허용 내원일자를 입력해주세요.';
        if (ftdt > todt) return '전송마감예외기간 시작일자는 종료일자보다 빨라야 합니다.';
        if (fidt > tidt) return '전송허용 내원일자 시작일자는 종료일자보다 빨라야 합니다.';
        if (fidt > ftdt) return '전송허용 내원일자 시작일자는 전송마감예외기간 시작일자보다 빨라야 합니다.';
        if (tidt > todt) return '전송허용 내원일자 종료일자는 전송마감예외기간 종료일자보다 빨라야 합니다.';
        if (ftdt + form.emmgFttm === todt + form.emmgTotm) return '전송마감예외기간의 시작일시와 종료일시가 동일합니다.';
        if (isUpdate && todayStr() >= form.emmgFtdtOld.replace(/-/g, '')) return '전송마감예외기간이 도래하여 수정할 수 없습니다.';

        return null;
    };

    const save = async () => {
        const message = validate();
        if (message) {
            showNotiModal(message);
            return;
        }

        if (form.emogAll === 'Y') {
            const ok = await confirm('전체 기관에 대해 동일하게 적용하시겠습니까?');
            if (!ok) return;
        } else {
            const ok = await confirm(isUpdate ? '수정하시겠습니까?' : '등록하시겠습니까?');
            if (!ok) return;
        }

        try {
            const { data } = await axios.post('/ptmiapi/system/nedis/codeweb/bdexceptransSave.do', {
                smode: smode,
                emmgEmcd: form.searchEmog,
                emmgFtdt: form.emmgFtdt,
                emmgFttm: form.emmgFttm,
                emmgTodt: form.emmgTodt,
                emmgTotm: form.emmgTotm,
                emmgFidt: form.emmgFidt,
                emmgTidt: form.emmgTidt,
                emogAll: form.emogAll,
                emmgDtcd: form.emmgDtcd,
                emmgMemo: form.emmgMemo,
                emmgFtdtOld: form.emmgFtdtOld,
                emmgTodtOld: form.emmgTodtOld,
            });

            showNotiModal(data.resultMessage);
            if (!data.hasError) {
                modalClose();
                setReSearch(true);
            }
        } catch (error) {
            console.error('전송기준 예외관리 저장 중 오류 발생:', error);
            showNotiModal('저장에 실패하였습니다.');
        }
    };

    const remove = async () => {
        if (todayStr() >= form.emmgFtdtOld.replace(/-/g, '')) {
            showNotiModal('전송마감예외기간이 도래하여 삭제할 수 없습니다.');
            return;
        }

        const ok = await confirm('선택한 전송기준 예외정보를 삭제하시겠습니까?');
        if (!ok) return;

        try {
            const { data } = await axios.post('/ptmiapi/system/nedis/codeweb/bdexceptransDel.do', {
                emmgEmcd: form.searchEmog,
                emmgFtdtOld: form.emmgFtdtOld,
                emmgTodtOld: form.emmgTodtOld,
            });

            showNotiModal(data.resultMessage);
            if (!data.hasError) {
                modalClose();
                setReSearch(true);
            }
        } catch (error) {
            console.error('전송기준 예외관리 삭제 중 오류 발생:', error);
            showNotiModal('삭제에 실패하였습니다.');
        }
    };

    // esc 키 누르면 모달 닫히는 기능
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') modalClose();
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [modalClose]);

    if (!isModalOpen.modalState) return null;

    return (
        <div className='layer_container excepttrans_layer'>
            <div className='layer_wrapper'>
                <div className='modal_title_box'>
                    <h4>{isUpdate ? '전송기준 예외정보 수정' : '전송기준 예외정보 신규 등록'}</h4>
                </div>
                <hr />
                <div className='com-search'>
                    <table>
                        <colgroup>
                            <col width='16%' />
                            <col width='18%' />
                            <col width='12%' />
                            <col width='18%' />
                            <col width='12%' />
                            <col width='24%' />
                        </colgroup>
                        <tbody>
                            {isUpdate ?
                                <tr>
                                    <th>센터구분</th>
                                    <td>{row?.EMOGGRDCNM}</td>
                                    <th>지역</th>
                                    <td>{row?.EMOGLOCANM}</td>
                                    <th>기관명</th>
                                    <td>{row?.EMOGDESC}</td>
                                </tr>
                                :
                                <tr>
                                    <NewCommonSelect
                                        hospitalTF={true}
                                        searchParams={form}
                                        handleInputChange={handleInputChange}
                                    />
                                </tr>
                            }
                            <tr>
                                <th>전송마감 예외기간</th>
                                <td colSpan={5}>
                                    <input type='date' name='emmgFtdt' value={form.emmgFtdt} onChange={handleInputChange} />&nbsp;
                                    <select name='emmgFttm' value={form.emmgFttm} onChange={handleInputChange}>
                                        {timeOptions.map(t => <option key={t} value={t}>{t}시</option>)}
                                    </select>
                                    &nbsp;~&nbsp;
                                    <input type='date' name='emmgTodt' value={form.emmgTodt} onChange={handleInputChange} min={form.emmgFtdt} />&nbsp;
                                    <select name='emmgTotm' value={form.emmgTotm} onChange={handleInputChange}>
                                        {timeOptions.map(t => <option key={t} value={t}>{t}시</option>)}
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <th>전송허용 내원일자</th>
                                <td colSpan={5}>
                                    <input type='date' name='emmgFidt' value={form.emmgFidt} onChange={handleInputChange} />
                                    &nbsp;~&nbsp;
                                    <input type='date' name='emmgTidt' value={form.emmgTidt} onChange={handleInputChange} min={form.emmgFidt} />
                                </td>
                            </tr>
                            <tr>
                                <th>전체기관 적용</th>
                                <td colSpan={5}>
                                    <input type='checkbox' id='emogAll' name='emogAll' checked={form.emogAll === 'Y'} onChange={handleInputChange} />
                                    <label htmlFor='emogAll'>&nbsp;전체기관 동일 적용</label>
                                </td>
                            </tr>
                            <tr>
                                <th>자료삭제</th>
                                <td colSpan={5}>
                                    <input type='radio' id='emmgDtcd1' name='emmgDtcd' value='1' checked={form.emmgDtcd === '1'} onChange={handleInputChange} />
                                    <label htmlFor='emmgDtcd1'>&nbsp;전체정보 삭제허용&nbsp;&nbsp;</label>
                                    <input type='radio' id='emmgDtcd2' name='emmgDtcd' value='2' checked={form.emmgDtcd === '2'} onChange={handleInputChange} />
                                    <label htmlFor='emmgDtcd2'>&nbsp;퇴원정보만 삭제허용</label>
                                </td>
                            </tr>
                            <tr>
                                <th>사유</th>
                                <td colSpan={5}>
                                    <input type='text' className='w-full input_style' name='emmgMemo' value={form.emmgMemo} onChange={handleInputChange} maxLength={100} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <hr />
                <div className='modal_btn_box'>
                    <button type='button' className={`modal_btn cursor-pointer ${primaryBackColor}`} onClick={save}>저장</button>
                    {isUpdate && <button type='button' className='modal_btn bg_tr_primary cursor-pointer' onClick={remove}>삭제</button>}
                    <button type='button' className='modal_btn bg_co_black cursor-pointer' onClick={modalClose}>닫기</button>
                </div>
            </div>
        </div>
    );
};

export default BdExceptTrans_layer;
